import { request } from 'Lib/axios'
import { useQuery } from '@tanstack/react-query'
import { ExtractFnReturnType } from 'Lib/react-query'
import { useUserStore } from 'Stores/userStoreModuleFed'
import { CurrenciesTypes } from 'Types/Currencies'
import { ProductTypes, ProductTypesWithoutBundle } from 'Types/ProductTypes'

export type PaymentStatus = 'active' | 'trialing' | 'past_due' | 'incomplete_expired'

export interface ProductType {
  id: number
  currency: CurrenciesTypes
  pricingDescription: string
  pricingId: string
  amount: number
  product: ProductTypes
  type: string
  limit: number
  domains: number
  billingCycleInMonths: number
}

export interface TrailType {
  active: boolean
  endTime: string
  startTime: string
  extendRequest: number | undefined | null
  productName: null | ProductTypesWithoutBundle
}

export interface PlansDetailsType {
  subscriptionId: number
  billingSubscriptionId: string
  billingUpdated: boolean
  startDate: string
  endDate: string
  currentPeriodStart: string
  currentPeriodEnd: string
  status: PaymentStatus
  plans: Array<ProductType>
  cancelled: boolean
  cancellationDate?: string
  trial: TrailType
  totalAmount: number
  noCreditCard: Date | null
  oneTimeFee?: number
  productsAmountWithDiscount?: number
  installmentMonth?: number
  productCancelled: Record<ProductTypesWithoutBundle, boolean>
  paymentMethod: string
  netAmountToPay?: number
}

const getPlansDetails = async (accountId: number) =>
  await request<{ data: PlansDetailsType }>({
    url: `accounts/${accountId}/subscriptions/plans/details`,
    method: 'GET',
  })

type QueryFnType = typeof getPlansDetails

export const useGetPlansDetails = () => {
  const { accountId, subscriptionId, setInstallmentsSubscription } = useUserStore()
  const query = useQuery<ExtractFnReturnType<QueryFnType>>({
    queryFn: () => getPlansDetails(accountId),
    queryKey: ['getPlansDetails', accountId],
    enabled: !!accountId && !!subscriptionId,
    onSuccess: (data) => {
      if (!(data instanceof Error && data) && data.data.plans) {
        const plans = data.data.plans
        setInstallmentsSubscription(plans.some((plan) => plan.billingCycleInMonths === 1))
      }
    },
  })
  const data = query.data instanceof Error ? undefined : query.data?.data

  return { ...query, data }
}
